import type { IChatRecoveryStatus } from "@/api/chat/chat.types";

export type DocumentWaitCounts = {
    /** Frozen at admission; a terminal, deleted or failed document never lowers it. */
    total: number;
    /**
     * Never rendered. It survives as the skew signal: a `remaining` above
     * `total` is client and server disagreeing, which makes the pair
     * untrustworthy for choosing between the singular and plural sentence.
     */
    remaining: number;
};

export const DOCUMENT_WAIT_TITLE_ONE = "Waiting for your document";
export const DOCUMENT_WAIT_TITLE_MANY = "Waiting for your documents";
export const DOCUMENT_WAIT_BODY_ONE =
    "Polly will answer as soon as it finishes processing.";
export const DOCUMENT_WAIT_BODY_MANY =
    "Polly will answer as soon as they finish processing.";

/**
 * The card's release line, and the reason it carries no meter.
 *
 * The park runs against the server's own stored deadline and the sweep answers
 * it with nobody watching, so nothing on this card may imply that staying makes
 * it finish.
 *
 * It promises a saved answer rather than a notification, because there is no
 * notification: no browser notification, no email, no unread marker, and no
 * poll that outlives this component. What is true is that the sweep writes the
 * answer to the conversation, so it is there whenever the person opens the
 * thread again. Do not upgrade this sentence to "we will let you know" without
 * shipping something that actually does.
 */
export const DOCUMENT_WAIT_NOTE =
    "You can leave this chat — your answer will be waiting here.";

/** Counts may be absent or skewed, so the plural is the safe default. */
export function documentWaitTitle(counts: DocumentWaitCounts | null): string {
    return counts?.total === 1
        ? DOCUMENT_WAIT_TITLE_ONE
        : DOCUMENT_WAIT_TITLE_MANY;
}

/** Counts may be absent or skewed, so the plural is the safe default. */
export function documentWaitBody(counts: DocumentWaitCounts | null): string {
    return counts?.total === 1
        ? DOCUMENT_WAIT_BODY_ONE
        : DOCUMENT_WAIT_BODY_MANY;
}

/** The server parked this turn behind documents it is still processing. */
export function isDocumentWait(
    status: IChatRecoveryStatus | undefined
): boolean {
    return status?.waiting_for_documents === true;
}

function isCount(value: number | null | undefined): value is number {
    return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

/**
 * The pair of counts, or null when this read carries none that agree.
 *
 * A wait froze at least one document and remaining can only fall towards total,
 * so an absent count, a zero total or an over-total remaining is skew between
 * client and server. The pair decides one thing only -- whether the card says
 * "document" or "documents", in the title and the body together -- and the
 * plural is the safe reading of a pair that cannot be trusted.
 */
export function documentWaitCounts(
    status: IChatRecoveryStatus | undefined
): DocumentWaitCounts | null {
    if (!isDocumentWait(status) || status === undefined) return null;
    const total = status.waiting_documents_total;
    const remaining = status.waiting_documents_remaining;
    if (!isCount(total) || !isCount(remaining)) return null;
    if (total === 0 || remaining > total) return null;
    return { total, remaining };
}
